'use client';

/**
 * Press, hold and drag an edge button — the app menu, search, chat or the
 * player toggle — across the middle of the screen to swap sides (Gordon
 * 2026-10-31: "click hold drag app menu button and change place with search
 * button as well as chat and play button in footer"). It flips the same
 * left/right-handed layout as Settings → Layout → Menu side, so every
 * mirrored element follows. Saved on the device, and on the account when
 * signed in.
 *
 * A plain tap still works as before: the drag only starts after a 350 ms
 * hold without moving, and the click that ends a drag is swallowed.
 */

import { useCallback, useRef, type CSSProperties, type MouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useOptionalUserLayoutPrefs } from '@/contexts/user-layout-prefs-context';

const HOLD_MS = 350;
const SLOP_PX = 10;

const DRAG_STYLE: CSSProperties = { touchAction: 'none', WebkitTouchCallout: 'none', userSelect: 'none' };

export function useHoldDragSwap() {
  const prefs = useOptionalUserLayoutPrefs();
  const live = useRef(prefs);
  live.current = prefs;
  const suppressClick = useRef(false);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const el = e.currentTarget;
    const html = document.documentElement;
    const x0 = e.clientX, y0 = e.clientY;
    const fromLeft = x0 < window.innerWidth / 2;
    let dragging = false, ready = false;
    // A floating copy follows the finger: the real button may sit inside a
    // clipped sidebar or the header's stacking context.
    let ghost: HTMLElement | null = null;

    const hold = setTimeout(() => {
      dragging = true;
      const r = el.getBoundingClientRect();
      ghost = el.cloneNode(true) as HTMLElement;
      ghost.removeAttribute('id');
      ghost.setAttribute('aria-hidden', 'true');
      Object.assign(ghost.style, {
        position: 'fixed', left: `${r.left}px`, top: `${r.top}px`, right: 'auto', bottom: 'auto',
        width: `${r.width}px`, height: `${r.height}px`, margin: '0', zIndex: '300',
        pointerEvents: 'none', transition: 'none', transform: 'scale(1.08)',
        boxShadow: '0 8px 24px rgb(0 0 0 / 0.45)', opacity: '0.95',
      });
      document.body.appendChild(ghost);
      el.style.opacity = '0.35';
      html.setAttribute('data-bb-swap-drag', fromLeft ? 'to-right' : 'to-left');
      try { navigator.vibrate?.(15); } catch { /* unsupported */ }
    }, HOLD_MS);

    function move(ev: PointerEvent) {
      if (!dragging) {
        if (Math.hypot(ev.clientX - x0, ev.clientY - y0) > SLOP_PX) end(false);
        return;
      }
      ev.preventDefault();
      if (ghost) ghost.style.transform = `translate(${ev.clientX - x0}px, ${ev.clientY - y0}px) scale(1.08)`;
      const crossed = fromLeft ? ev.clientX > window.innerWidth / 2 : ev.clientX < window.innerWidth / 2;
      if (crossed !== ready) {
        ready = crossed;
        if (ready) html.setAttribute('data-bb-swap-ready', ''); else html.removeAttribute('data-bb-swap-ready');
      }
    }
    function end(commit: boolean) {
      clearTimeout(hold);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      html.removeAttribute('data-bb-swap-drag');
      html.removeAttribute('data-bb-swap-ready');
      ghost?.remove();
      ghost = null;
      el.style.opacity = '';
      if (!dragging) return;
      suppressClick.current = true;
      setTimeout(() => { suppressClick.current = false; }, 400);
      const p = live.current;
      if (commit && ready && p) void p.updateMenuSide(p.menuSide === 'right' ? 'left' : 'right');
    }
    function up() { end(true); }
    function cancel() { end(false); }

    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  }, []);

  const onClickCapture = useCallback((e: MouseEvent) => {
    if (!suppressClick.current) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // A long press must not open the browser's link / context menu.
  const onContextMenu = useCallback((e: MouseEvent) => { e.preventDefault(); }, []);

  return { onPointerDown, onClickCapture, onContextMenu, style: DRAG_STYLE };
}
